import type { ApiErrorBody } from '@okra/shared'

const API_BASE = process.env.NEXT_PUBLIC_API_URL ?? '/api/v1'

// In-memory access token (never stored in localStorage)
let accessToken: string | null = null

export function setAccessToken(token: string | null) {
  accessToken = token
}

export function getAccessToken(): string | null {
  return accessToken
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

interface FetchOptions extends RequestInit {
  skipAuth?: boolean
}

export async function fetchWithAuth(url: string, options: FetchOptions = {}): Promise<Response> {
  const { skipAuth = false, ...fetchOptions } = options

  const headers: Record<string, string> = {
    ...(fetchOptions.body !== undefined && fetchOptions.body !== null
      ? { 'Content-Type': 'application/json' }
      : {}),
    ...(fetchOptions.headers as Record<string, string>),
  }

  if (!skipAuth && accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }

  const response = await fetch(`${API_BASE}${url}`, {
    ...fetchOptions,
    headers,
    credentials: 'include', // Always send cookies (refresh token)
  })

  // Attempt token refresh on 401
  if (response.status === 401 && !skipAuth && !url.includes('/auth/')) {
    const refreshed = await tryRefreshToken()
    if (refreshed) {
      headers['Authorization'] = `Bearer ${accessToken}`
      return fetch(`${API_BASE}${url}`, { ...fetchOptions, headers, credentials: 'include' })
    }
    // Redirect to login if refresh fails
    if (typeof window !== 'undefined') {
      window.location.href = '/login'
    }
  }

  return response
}

async function tryRefreshToken(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    })
    if (!res.ok) return false
    const data = await res.json()
    setAccessToken(data.accessToken)
    return true
  } catch {
    return false
  }
}

async function parseResponse<T>(response: Response): Promise<T> {
  const text = await response.text()
  if (!text) return undefined as T

  let data: unknown
  try {
    data = JSON.parse(text)
  } catch {
    throw new ApiError(response.status, 'PARSE_ERROR', 'Failed to parse response')
  }

  if (!response.ok) {
    const err = data as ApiErrorBody
    throw new ApiError(
      response.status,
      err.error?.code ?? 'UNKNOWN_ERROR',
      err.error?.message ?? 'Unknown error',
      err.error?.details,
    )
  }

  return data as T
}

export const api = {
  get: async <T = any>(url: string, options?: FetchOptions): Promise<T> => {
    const res = await fetchWithAuth(url, { ...options, method: 'GET' })
    return parseResponse<T>(res)
  },

  post: async <T = any>(url: string, body?: unknown, options?: FetchOptions): Promise<T> => {
    const res = await fetchWithAuth(url, {
      ...options,
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
    return parseResponse<T>(res)
  },

  patch: async <T = any>(url: string, body?: unknown, options?: FetchOptions): Promise<T> => {
    const res = await fetchWithAuth(url, {
      ...options,
      method: 'PATCH',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
    return parseResponse<T>(res)
  },

  put: async <T = any>(url: string, body?: unknown, options?: FetchOptions): Promise<T> => {
    const res = await fetchWithAuth(url, {
      ...options,
      method: 'PUT',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
    return parseResponse<T>(res)
  },

  delete: async <T = any>(url: string, options?: FetchOptions): Promise<T> => {
    const res = await fetchWithAuth(url, { ...options, method: 'DELETE' })
    return parseResponse<T>(res)
  },
}
